/**
 * Dismiss the global notification toast when the user moves to a different page.
 *
 * Jake (2026-08-26), verbatim: "When an error snackbar is showing, it should be
 * dismissed when changing routes -- i.e. the snackbar shouldn't hang around
 * after moving to a different page."
 *
 * This SCOPES the deliberate error persistence in `store/notification.js`; it
 * does not remove it. An error toast is created with timeout -1 precisely so it
 * survives until the user reads or acts on it (errors often carry an action to
 * take), and that stays true for as long as the user is on the page that raised
 * it. What it must not do is OUTLIVE that page: an error about a form the user
 * has already left is noise sitting over the next page's UI.
 *
 * The subtlety that makes this more than a one-line afterEach
 * -------------------------------------------------------------
 * Several call sites raise an error and then navigate away, so the message
 * explains WHY the user was bounced. Clearing on every navigation would swallow
 * exactly those messages -- the user would be dumped on a different page with no
 * explanation. That is the one way this change could make the product WORSE, so
 * it is the case the design is built around. Enumerated 2026-08-26:
 *
 *   - `TheLoginPage.vue:573` and `Account/AccountAddEdit.vue:222` raise and push
 *     in the same synchronous turn;
 *   - `account/DistributorAddEdit.vue` (x2) and
 *     `account/DistributorAccountInfo.vue` (x2), plus one sibling, raise a
 *     message that NAMES the redirect ("Redirecting to distributor list") and
 *     push after a 1500ms setTimeout -- five such messages in total.
 *
 * (`store-editor/StoreAddEdit.vue:2707` is the mirror image -- it pushes and
 * THEN raises, so its toast is raised after afterEach has already run and this
 * hook never sees it. It needs no special handling.)
 *
 * Those two situations are indistinguishable by route alone: in both, the toast
 * was raised on the old path and the user ends up on a new one. They are also
 * indistinguishable by any synchronous router signal -- `beforeEach` runs
 * asynchronously AFTER `router.push()` returns, so at raise time no navigation
 * is ever "in flight" (verified 2026-08-26).
 *
 * What separates them is how long the toast has actually been readable when the
 * navigation lands. The raise-then-navigate sites fall into two shapes, and both
 * must survive:
 *
 *   - immediate: raise and push in the same synchronous turn (`TheLoginPage`,
 *     `Account/AccountAddEdit`), so zero time passes and the toast never paints;
 *   - announced: raise a message that literally says "Redirecting to distributor
 *     list", then push after a 1500ms setTimeout (`account/DistributorAddEdit`
 *     x2, `account/DistributorAccountInfo` x2, plus one more of the same shape
 *     -- five such messages in the app). Here the toast HAS painted, so a simple
 *     "was it ever on screen?" test would destroy the message on arrival at the
 *     very page it names. Verified 2026-08-26: an earlier draft of this hook did
 *     exactly that.
 *
 * So the rule is a minimum readable lifetime rather than a paint flag:
 *
 *     a toast may be dismissed by a page change only once it has been
 *     readable for at least READABLE_MS.
 *
 * A younger toast rides the navigation and is dismissed by the next page change
 * instead, by which time it has comfortably outlived READABLE_MS. That covers
 * both shapes with one concept and no list of special-cased call sites.
 *
 * READABLE_MS is 2000: longer than the 1500ms announced-redirect delay (so those
 * messages survive to be read on the page they name) and shorter than the 3000ms
 * TIMEOUT_TRANSIENT a success/info toast lives for anyway. A user who reads a
 * toast and then clicks a link takes far longer than 2s, so ordinary stale
 * toasts -- the case Jake reported -- are dismissed normally.
 *
 * Registered as `afterEach`, not `beforeEach`: `beforeEach` runs before a
 * navigation is committed, so a guard that redirects or aborts (this app has
 * several -- `requireToken`, `requireCapability`, `createStorefrontPasswordGate`)
 * would cost the user their toast for a page change that never happened. On a
 * redirect chain `afterEach` runs once, reporting the final destination.
 *
 * `afterEach` is necessary but NOT sufficient: it also fires for an ABORTED
 * navigation, reporting the destination that was never reached, so the hook
 * must check the `failure` argument as well (verified 2026-08-26 -- a test
 * caught this).
 *
 * Applies to every severity rather than branching on error-vs-success. One rule,
 * one mechanism: success/info already auto-dismiss after 3s, so this only
 * affects a navigation inside that window, where clearing is the same outcome
 * the user was going to get anyway, just sooner. A severity branch would buy
 * nothing and add a concept.
 *
 * Behavioural tests: `__tests__/dismiss-notification-on-route-change.spec.js`.
 */

// Minimum time a toast must have been readable before a page change may
// dismiss it. Above the 1500ms announced-redirect delay used by the
// distributor pages, below the 3000ms TIMEOUT_TRANSIENT in store/notification.js.
const READABLE_MS = 2000;

/**
 * Install the dismiss-on-page-change hook.
 *
 * @param {import('vue-router').Router} router
 * @param {import('vuex').Store} store
 * @param {object} [options]
 * @param {() => number} [options.now] Clock used to age the active toast.
 *   Defaults to `Date.now`. Injectable so tests can cross the readable-lifetime
 *   boundary deterministically instead of sleeping.
 */
export function installNotificationDismissOnRouteChange(router, store, options = {}) {
    const now = options.now || (() => Date.now());

    // Timestamp of the active toast, or null when none is showing. Compared
    // against READABLE_MS to decide whether a page change is allowed to
    // dismiss it; see the header for why elapsed time is the discriminator.
    let raisedAt = null;

    // Watch the store's own visibility flag rather than wrapping the raise
    // actions. Any path that shows a toast -- raiseError, raiseSuccess,
    // raiseInfo, or anything added later -- is captured automatically, so this
    // cannot drift out of sync with the notification module. `show` is the only
    // mutation of that name in the whole store and no module is namespaced
    // (verified 2026-08-26), so the type check is unambiguous.
    //
    // Guarded: this installer runs at router MODULE LOAD, and several existing
    // specs import `@/router` against a hand-rolled `@/store` stub that has
    // only state/getters/commit/dispatch. Hard-requiring `subscribe` there
    // turns an unrelated test's store double into an import-time crash. A store
    // without `subscribe` simply gets no dismiss-on-navigation behaviour, which
    // is the correct degradation for a stub.
    if (typeof store?.subscribe === 'function') {
        store.subscribe((mutation) => {
            if (mutation.type !== 'show') return;
            raisedAt = now();
        });
    }

    router.afterEach((to, from, failure) => {
        if (failure) {
            // The navigation was aborted or redirected away by a guard, so the
            // user never left the page. vue-router still fires afterEach in
            // that case, reporting the destination that was NOT reached
            // (verified 2026-08-26) -- dismissing here would take the toast
            // away for a page change that never happened. This app aborts
            // navigations in requireToken, requireCapability and
            // createStorefrontPasswordGate, so this is a live path.
            return;
        }

        if (!store.getters?.notificationIsActive) {
            // Nothing showing; nothing to dismiss. Optional-chained for the
            // same reason as the subscribe guard above -- a store double in an
            // unrelated spec may not carry these getters at all.
            return;
        }

        if (to.path === from.path) {
            // Not a page change. vue-router fires afterEach here too (a
            // redundant push, or a query-only change such as `?page=2`, or the
            // `?preview=1` / `?framed=1` this app's own guards re-attach on
            // in-app navigation -- verified 2026-08-26). The user has not moved
            // to a different page, so the toast stays.
            return;
        }

        if (raisedAt !== null && now() - raisedAt < READABLE_MS) {
            // Too young to have been read: either raised in the same turn as
            // this navigation, or an announced redirect whose message names the
            // page being navigated to. Either way the message exists to explain
            // THIS navigation, so it rides along to be read at the destination.
            // The next page change dismisses it, by which point it is well past
            // READABLE_MS.
            return;
        }

        // A real page change, and the user has already had the toast on screen.
        // This is the case Jake reported.
        //
        // Reuse the store's existing dismissal path -- the same action the
        // snackbar's close button and its `update:model-value` binding already
        // dispatch (`App.vue`) -- so a toast has exactly one way to go away.
        store.dispatch('closeErrorToaster');
        raisedAt = null;
    });
}
